import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Building2, Users } from "lucide-react";

export default function EmployerAuth() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) throw error;
      
      toast.success("Welcome back!");
      navigate("/employer/dashboard");
    } catch (error: any) {
      toast.error(error.message || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="p-6 border-b">
        <div className="flex items-center gap-2">
          <Building2 className="w-8 h-8 text-[hsl(var(--employer-navy))]" />
          <h1 className="text-2xl font-bold text-[hsl(var(--employer-navy))]">RePath</h1>
        </div>
      </header>

      {/* Main Content - Two Column Layout */}
      <div className="flex-1 flex">
        {/* Left Column - Branding & Value Props */}
        <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-[hsl(var(--employer-navy))] to-[hsl(var(--employer-navy))]/80 text-white p-12 flex-col justify-center relative overflow-hidden">
          {/* Overlay tint */}
          <div className="absolute inset-0 bg-[hsl(var(--employer-navy))]/40"></div>
          
          {/* Background illustration of team collaboration */}
          <div className="absolute inset-0 opacity-20">
            <Users className="absolute top-20 right-20 w-32 h-32" />
            <Users className="absolute bottom-32 left-16 w-24 h-24" />
          </div>

          <div className="relative z-10 space-y-8">
            <div>
              <h2 className="text-4xl font-bold mb-4">Unlock Untapped Potential</h2>
              <p className="text-lg text-white/90">
                Access a pre-vetted, motivated talent pool ready to contribute to your success.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[hsl(var(--employer-teal))] flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Quality Candidates</h3>
                  <p className="text-white/80">Connect with skilled professionals committed to their next chapter.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[hsl(var(--employer-teal))] flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Diverse Workforce</h3>
                  <p className="text-white/80">Build a team that reflects commitment and resilience.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Login Form */}
        <div className="flex-1 flex items-center justify-center p-8 md:p-12">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[hsl(var(--employer-navy))] mb-2">
                Welcome Back
              </h2>
              <p className="text-muted-foreground">
                Unlock untapped potential.
              </p>
            </div>

            <form onSubmit={handleSignIn} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Work Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="your.name@company.com"
                  className="border-border"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Enter your password"
                  className="border-border"
                  required
                />
              </div>

              <div className="text-right">
                <a href="#" className="text-sm text-[hsl(var(--employer-teal))] hover:underline">
                  Forgot Password?
                </a>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[hsl(var(--employer-teal))] text-white hover:bg-[hsl(var(--employer-teal))]/90 font-semibold py-6 text-lg"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                New to RePath?{" "}
                <a href="/employer-signup" className="text-[hsl(var(--employer-navy))] hover:underline font-semibold">
                  Create a Company Account
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="p-6 border-t">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
          <a href="/privacy" className="hover:text-foreground transition-colors">
            Privacy Policy
          </a>
          <a href="/terms" className="hover:text-foreground transition-colors">
            Terms of Service
          </a>
          <a href="mailto:admin@sccapp.co.uk" className="hover:text-foreground transition-colors">
            Contact Support
          </a>
        </div>
      </footer>
    </div>
  );
}
